import React, { useState, useEffect } from "react";
import I18n from "i18n";
import Usage from "models/usage";
import RtvPriceOfNextUnit from "models/rtvPriceOfNextUnit";
import Analytics from "libraries/analytics";
import RecordingOrder from "libraries/recordingOrder";
import OnlineClipOrder from "libraries/onlineClipOrder";
import Notificator from "libraries/notificator";
import Shortcutter from "libraries/shortcutter";
import RtvOrderModalView from "views/agents/rtvOrderModalView";
import OnlineClipOrderModalView from "views/agents/onlineClipOrderModalView";
import RtvOrderNoQuotaModalView from "views/agents/rtvOrderNoQuotaModalView";

interface Props {
  agentResult: any; // Backbone.Model
}

export default function DatalakeDetailLicensing({ agentResult }: Props) {
  const [modalVisible, setModalVisible] = useState<
    null | "rtv" | "online" | "noquota"
  >(null);
  const [rtvUpgradePrice, setRtvUpgradePrice] = useState<number | null>(null);

  const isOrdered = agentResult.isOrdered();
  const isOrderableOnlineClip = agentResult.isOrderableOnlineClip();

  async function handleOrderClick() {
    if (agentResult.isRtvClip()) {
      await verifyUsageLimit();
    } else if (isOrderableOnlineClip) {
      setModalVisible("online");
    }
  }

  async function verifyUsageLimit() {
    try {
      const [usageResult, nextUnitResult] = await Promise.all([
        new Usage().load(),
        new RtvPriceOfNextUnit().load(),
      ]);
      if (
        usageResult.rtv_recording_usage.usage >=
        usageResult.rtv_recording_usage.limit
      ) {
        setModalVisible("noquota");
      } else {
        setRtvUpgradePrice(nextUnitResult.price_of_next_unit);
        setModalVisible("rtv");
      }
    } catch (error) {
      Notificator.showNotification(
        I18n.t("webapp.notifications.error.order_failed")
      );
    }
  }

  async function handleRtvOrderConfirm(notes: string) {
    setModalVisible(null);
    try {
      await RecordingOrder.order(agentResult.id, notes);
      Notificator.showNotification(
        I18n.t("webapp.notifications.agent_results.order_successful")
      );
      agentResult.set({ ordered: true });
      Analytics.sendEvent("br_agent_result_ordered", {
        kind: agentResult.get("kind"),
      });
    } catch {
      Notificator.showNotification(
        I18n.t("webapp.notifications.error.order_failed")
      );
    }
  }

  async function handleOnlineClipOrderConfirm() {
    setModalVisible(null);
    try {
      await OnlineClipOrder.order(agentResult.id);
      Notificator.showNotification(
        I18n.t("webapp.agents.results.order_full_text.order_successful")
      );
      agentResult.set({ ordered: true });
      Analytics.sendEvent("br_agent_result_ordered", {
        kind: agentResult.get("kind"),
      });
    } catch {
      Notificator.showNotification(
        I18n.t("webapp.notifications.error.order_failed")
      );
    }
  }

  // Licensing shortcut: 'l'
  useEffect(() => {
    const handleLicensingShortcut = () => {
      if (agentResult.isOrderable() && agentResult.isOrderableOnlineClip()) {
        setModalVisible("online");
      }
    };
    Shortcutter.bindInModule("agents", "l", handleLicensingShortcut);

    return () => {
      Shortcutter.unbindInModule("agents", "l");
    };
  }, [agentResult]);

  useEffect(() => {
    let modalInstance: any = null;
    if (modalVisible === "rtv" && rtvUpgradePrice !== null) {
      modalInstance = new RtvOrderModalView({
        upgradePrice: rtvUpgradePrice,
        onConfirmed: handleRtvOrderConfirm,
        onClose: () => setModalVisible(null),
      });
      modalInstance.render();
      modalInstance.showModal();
      modalInstance.on("modal:confirmed", handleRtvOrderConfirm);
      modalInstance.on("modal:cancelled", () => setModalVisible(null));
    } else if (modalVisible === "online") {
      modalInstance = new OnlineClipOrderModalView({
        onConfirmed: handleOnlineClipOrderConfirm,
        onClose: () => setModalVisible(null),
      });
      modalInstance.render();
      modalInstance.showModal();
      modalInstance.on("modal:confirmed", handleOnlineClipOrderConfirm);
      modalInstance.on("modal:cancelled", () => setModalVisible(null));
    } else if (modalVisible === "noquota") {
      modalInstance = new RtvOrderNoQuotaModalView({
        onClose: () => setModalVisible(null),
      });
      modalInstance.render();
      modalInstance.showModal();
      modalInstance.on("modal:cancelled", () => setModalVisible(null));
    }
    return () => {
      if (modalInstance) {
        modalInstance.close();
      }
    };
  }, [modalVisible, rtvUpgradePrice]);

  return (
    <>
      <a
        className="link-bar__left-item"
        data-clip-order
        onClick={handleOrderClick}
        style={{ cursor: isOrdered ? "default" : "pointer" }}
      >
        {!isOrdered && (
          <>
            <i className="icon-order"></i>{" "}
            {isOrderableOnlineClip
              ? I18n.t("webapp.agents.results.order_full_text.license")
              : I18n.t("webapp.agents.results.result_details.order")}
          </>
        )}
      </a>
    </>
  );
}
